/**
 * WebSocket hook for real-time cross-terminal sync
 *
 * Connects to /ws on the same origin. When any terminal makes a change
 * (buy/sell/condition/candidate/market update), the server broadcasts
 * a push event to all connected WebSocket clients. This hook receives
 * those events and triggers store refreshes.
 *
 * If WebSocket is unavailable (dev mode without server support), it
 * silently falls back to the existing polling mechanism.
 */
import { useEffect, useRef, useCallback } from 'react';
import { useMarketStore } from '../store/marketStore';
import { useTradeStore } from '../store/tradeStore';

interface WSMessage {
  type: 'candidates_updated' | 'trade_executed' | 'conditions_changed' | 'market_updated' | 'account_changed' | 'connected';
  data?: any;
  timestamp: string;
}

export function useWebSocketSync() {
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectDelay = useRef(1000);
  const reconnectTimer = useRef<number | null>(null);

  const handleMessage = useCallback((msg: WSMessage) => {
    const marketStore = useMarketStore.getState();
    const tradeStore = useTradeStore.getState();

    switch (msg.type) {
      case 'candidates_updated':
        tradeStore.fetchCandidates();
        break;
      case 'trade_executed':
        tradeStore.fetchHoldings();
        tradeStore.fetchAccount();
        tradeStore.fetchCandidates();
        break;
      case 'conditions_changed':
        // Could refresh conditions page if needed
        tradeStore.fetchCandidates();
        break;
      case 'market_updated':
        marketStore.fetchQuotes();
        marketStore.fetchBreadth();
        break;
      case 'account_changed':
        tradeStore.fetchAccount();
        tradeStore.fetchHoldings();
        break;
      default:
        break;
    }
  }, []);

  const connect = useCallback(() => {
    // Build WebSocket URL from current origin
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const wsUrl = `${protocol}//${window.location.host}/ws`;

    try {
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onopen = () => {
        console.log('[WS] Real-time sync connected');
        reconnectDelay.current = 1000; // reset backoff
        ws.send(JSON.stringify({ type: 'subscribe' }));
      };

      ws.onmessage = (event) => {
        try {
          const msg = JSON.parse(event.data) as WSMessage;
          handleMessage(msg);
        } catch {
          // ignore parse errors
        }
      };

      ws.onclose = () => {
        console.log('[WS] Disconnected, will retry...');
        wsRef.current = null;
        scheduleReconnect();
      };

      ws.onerror = () => {
        // Error handled by onclose
      };
    } catch {
      // WebSocket not available (e.g., dev mode), fall back to polling
      wsRef.current = null;
    }
  }, [handleMessage]);

  const scheduleReconnect = useCallback(() => {
    if (reconnectTimer.current) return;
    // Exponential backoff: 1s, 2s, 4s, 8s, 16s, max 30s
    const delay = Math.min(reconnectDelay.current, 30000);
    reconnectTimer.current = window.setTimeout(() => {
      reconnectTimer.current = null;
      reconnectDelay.current = Math.min(reconnectDelay.current * 2, 30000);
      connect();
    }, delay);
  }, [connect]);

  useEffect(() => {
    connect();
    return () => {
      if (reconnectTimer.current) {
        clearTimeout(reconnectTimer.current);
      }
      if (wsRef.current) {
        wsRef.current.close();
      }
    };
  }, [connect]);
}
